import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import moment from 'moment';
import React, { FC } from 'react';
import { useRecoilValue } from 'recoil';

import {
  borderB,
  borderClassName,
  borderColorClassName,
  borderR,
  borderT,
  headerHeight,
} from '@/components/Subscription/PlanTable';
import Cell from '@/components/Subscription/PlanTable/Columns/Cell';
import H1Cell from '@/components/Subscription/PlanTable/Columns/H1Cell';
import H2Cell from '@/components/Subscription/PlanTable/Columns/H2Cell';
import HeaderCell from '@/components/Subscription/PlanTable/Columns/HeaderCell';
import MainHCell from '@/components/Subscription/PlanTable/Columns/MainHCell';

import { user } from '@/atoms/user';
import { getSubPlanName } from '@/pages/gems-transactions';
import { isSubscriptionActive } from '@/utils/index';
import {
  IPlan,
  PeriodicityTypeEnum,
  PlanNameEnum,
} from '@/utils/interfaces/Subscription';
import { User } from '@/utils/interfaces/User';

export const columnWidth = 251;
export const columnWidthSmall = 158;

export enum SubscriptionButtonTitle {
  Subscribed = 'Subscribed',
  Re_Subscribe = 'Re-Subscribe',
  Cancel = 'Cancel',
  Change_Plan = 'Change Plan',
  Select = 'Select',
}

export const getStructuredPlanData = (
  plan: IPlan,
  userState: User | undefined,
) => {
  const obj = {
    title: `${plan?.periodicity} ${plan?.periodicity_type === PeriodicityTypeEnum.monthly ? 'Month' : ''} Plan`,
    price: plan?.razorpay_plan
      ? (plan?.razorpay_plan?.amount / 100).toString()
      : '',
    whatInside: plan?.features[0]?.quota
      ? `Pick any 1 Book Every Month for ${plan?.periodicity} Months`
      : '',
    digital: plan?.features[0]?.quota
      ? (plan?.features[0]?.quota * plan?.periodicity).toString()
      : '',
    hard:
      plan?.features[0]?.quota && plan.rules[0]?.physical
        ? (plan?.features[0]?.quota * plan?.periodicity).toString()
        : '0',
    duration: `${plan?.periodicity} ${plan?.periodicity_type === PeriodicityTypeEnum.monthly ? 'Months' : ''}`,
    validity: plan?.rules[0]?.has_expiry
      ? plan.periodicity +
        ' ' +
        (plan?.periodicity_type === PeriodicityTypeEnum.monthly ? 'Months' : '')
      : 'Life Time Access',
    freeShipping: true,
    appAccess: true,
    isSubscribed: userState?.subscription?.plan_id === plan.id,
    buttonTooltip:
      userState?.subscription?.plan_id === plan.id &&
      userState.features[0]?.remaining_quantity !== 0
        ? 'Already Subscribed'
        : isSubscriptionActive(userState) &&
            userState?.features[0]?.remaining_quantity !== 0
          ? 'Please use all your remaining gems before switching plans.'
          : '',
    buttonTitle: (
      userState?.subscription?.plan_id === plan.id &&
      moment(userState?.subscription.expired_at).diff(moment(), 'days') <= 30
        ? SubscriptionButtonTitle.Re_Subscribe
        : userState?.subscription?.plan_id === plan.id &&
          userState.features[0]?.remaining_quantity === 0
    )
      ? SubscriptionButtonTitle.Re_Subscribe
      : userState?.subscription?.plan_id === plan.id
        ? SubscriptionButtonTitle.Subscribed
        : userState?.subscription?.plan_id
          ? SubscriptionButtonTitle.Change_Plan
          : SubscriptionButtonTitle.Select,
    disabled:
      userState?.subscription?.plan_id === plan.id &&
      moment(userState?.subscription.expired_at).diff(moment(), 'days') <= 30
        ? false
        : (userState?.subscription?.plan_id === plan.id &&
            userState.features[0]?.remaining_quantity !== 0) ||
          (isSubscriptionActive(userState) &&
            userState?.features[0]?.remaining_quantity !== 0),
    bestValue: plan.name === PlanNameEnum.Premium,
    expiresIn:
      userState?.subscription?.expired_at &&
      userState?.subscription?.plan_id === plan.id
        ? `Expires in ${moment(userState.subscription.expired_at).diff(moment(), 'days')} days`
        : '',
  };
  return obj;
};

interface Props {
  plans?: IPlan[];
  setSelectedPlan: React.Dispatch<React.SetStateAction<IPlan | undefined>>;
  subscriptionHandle: (buttonTitle: SubscriptionButtonTitle) => Promise<void>;
}
const Columns: FC<Props> = ({ plans, setSelectedPlan, subscriptionHandle }) => {
  const screens = useBreakpoint();
  const stPlan = plans?.find((i) => i.name === PlanNameEnum.Standard);
  const pmPlan = plans?.find((i) => i.name === PlanNameEnum.Premium);
  const userState = useRecoilValue(user);

  return (
    <div
      className={`
  ${borderClassName}
  ${borderColorClassName}
  rounded-xl flex  overflow-hidden flex-col box-border
  `}
    >
      <div style={{ height: headerHeight }} className='flex flex-col'>
        <MainHCell
          subTitle='Digital + Hard Cover'
          title={stPlan ? getSubPlanName(stPlan?.name) : ''}
        />
        <div className='flex flex-1'>
          <HeaderCell
            bestValue={
              stPlan
                ? getStructuredPlanData(stPlan, userState).bestValue
                : false
            }
            disabled={
              stPlan ? getStructuredPlanData(stPlan, userState).disabled : false
            }
            classes={{
              divClass: `
            ${borderColorClassName}
            ${borderR}
            `,
              headerClass: `
            ${borderColorClassName}
            ${borderR}
            `,
              bestValueClass: `
            ${borderColorClassName}
            ${borderR}
            `,
            }}
            buttonTitle={
              stPlan ? getStructuredPlanData(stPlan, userState).buttonTitle : ''
            }
            buttonTooltip={
              stPlan
                ? getStructuredPlanData(stPlan, userState).buttonTooltip
                : ''
            }
            price={stPlan ? getStructuredPlanData(stPlan, userState).price : ''}
            priceCut='5070'
            title={stPlan ? getStructuredPlanData(stPlan, userState).title : ''}
            onClick={() => {
              setSelectedPlan(stPlan);
              stPlan &&
                subscriptionHandle(
                  getStructuredPlanData(stPlan, userState).buttonTitle,
                );
            }}
            expText={
              stPlan ? getStructuredPlanData(stPlan, userState).expiresIn : ''
            }
          />
          <HeaderCell
            bestValue={
              pmPlan
                ? getStructuredPlanData(pmPlan, userState).bestValue
                : false
            }
            disabled={
              pmPlan ? getStructuredPlanData(pmPlan, userState).disabled : false
            }
            buttonTitle={
              pmPlan ? getStructuredPlanData(pmPlan, userState).buttonTitle : ''
            }
            buttonTooltip={
              pmPlan
                ? getStructuredPlanData(pmPlan, userState).buttonTooltip
                : ''
            }
            price={pmPlan ? getStructuredPlanData(pmPlan, userState).price : ''}
            priceCut='20,280'
            title={pmPlan ? getStructuredPlanData(pmPlan, userState).title : ''}
            expText={
              pmPlan ? getStructuredPlanData(pmPlan, userState).expiresIn : ''
            }
            onClick={() => {
              setSelectedPlan(pmPlan);
              pmPlan &&
                subscriptionHandle(
                  getStructuredPlanData(pmPlan, userState).buttonTitle,
                );
            }}
          />
        </div>
      </div>
      <div>
        <div className='flex items-center'>
          <H1Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            ${borderT}
            ${borderB}
            `}
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
            title={
              stPlan ? getStructuredPlanData(stPlan, userState).whatInside : ''
            }
            classes={{
              pClass: 'text-center',
            }}
          />
          <H1Cell
            className={`
            ${borderColorClassName}
            ${borderT}
              ${borderB}
            `}
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
            title={
              pmPlan ? getStructuredPlanData(pmPlan, userState).whatInside : ''
            }
            classes={{
              pClass: 'text-center',
            }}
          />
        </div>
        <div className='flex items-center'>
          <H2Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            `}
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <H2Cell
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
        <div className='flex items-center'>
          <Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={
              stPlan ? getStructuredPlanData(stPlan, userState).digital : ''
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <Cell
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={
              pmPlan ? getStructuredPlanData(pmPlan, userState).digital : ''
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
        <div className='flex items-center'>
          <Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={stPlan ? getStructuredPlanData(stPlan, userState).hard : ''}
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <Cell
            className={`
            ${borderColorClassName}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={pmPlan ? getStructuredPlanData(pmPlan, userState).hard : ''}
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
        <div className='flex items-center'>
          <Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            ${borderT}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={
              stPlan ? getStructuredPlanData(stPlan, userState).duration : ''
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <Cell
            className={`
            ${borderColorClassName}
            ${borderT}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={
              pmPlan ? getStructuredPlanData(pmPlan, userState).duration : ''
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
        <div className='flex items-center'>
          <Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={
              stPlan ? getStructuredPlanData(stPlan, userState).validity : ''
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <Cell
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            title={
              pmPlan ? getStructuredPlanData(pmPlan, userState).validity : ''
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
        <div className='flex items-center'>
          <Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            icon={
              stPlan
                ? getStructuredPlanData(stPlan, userState).freeShipping
                : false
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <Cell
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            icon={
              pmPlan
                ? getStructuredPlanData(pmPlan, userState).freeShipping
                : false
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
        <div className='flex items-center'>
          <Cell
            className={`
            ${borderColorClassName}
            ${borderR}
            `}
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            icon={
              stPlan
                ? getStructuredPlanData(stPlan, userState).appAccess
                : false
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
          <Cell
            classes={{
              childDivClass: 'justify-center',
              pClass: 'text-center',
            }}
            icon={
              pmPlan
                ? getStructuredPlanData(pmPlan, userState).appAccess
                : false
            }
            style={{
              width: screens.xl ? `${columnWidth}px` : `${columnWidthSmall}px`,
              maxWidth: screens.xl
                ? `${columnWidth}px`
                : `${columnWidthSmall}px`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Columns;
